import { useState, useCallback } from "react";
import { Sparkles, RefreshCw, Target, ArrowRight, Save } from "lucide-react";
import { useCentralState } from "@/context/CentralStateContext";
import { usePeriod } from "@/context/PeriodContext";
import { computeCentralSnapshot, statusStyles, centerMeta } from "@/lib/central";
import { periodLabel } from "@/lib/wellness";
import { centralApi } from "@/lib/api";
import { PageHeader, SectionCard, EmptyState } from "@/components/shared";
import { PeriodNav } from "@/components/PeriodNav";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function CentralAI() {
  const { fin, life, wellness, loading } = useCentralState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const [byHorizon, setByHorizon] = useState({});
  const [busy, setBusy] = useState(false);

  const result = byHorizon[horizon];

  const generate = useCallback(async () => {
    if (!fin) return;
    setBusy(true);
    try {
      const snap = computeCentralSnapshot(fin, life, wellness, date);
      const res = await centralApi.overview({ horizon, date, metrics: snap });
      setByHorizon((prev) => ({ ...prev, [horizon]: res }));
    } catch { toast.error("Couldn't generate your overview right now"); }
    finally { setBusy(false); }
  }, [fin, life, wellness, date, horizon]);

  const saveReport = async () => {
    if (!result) return;
    try {
      const snap = computeCentralSnapshot(fin, life, wellness, date);
      await centralApi.saveReport({ title: `${horizon} AI overview — ${periodLabel(horizon, date)}`, horizon, date, snapshot: snap, ai: result });
      toast.success("Saved to reports");
    } catch { toast.error("Could not save report"); }
  };

  if (loading || !fin) return <Skeleton className="h-96 rounded-[14px]" />;

  return (
    <div data-testid="central-ai">
      <PageHeader title="Strategist AI" subtitle="How you're doing across all three centers — with what to focus on next." testid="central-ai-header"
        actions={result && <Button variant="outline" size="sm" onClick={saveReport} data-testid="ai-save-report-button" className="border-[color:var(--border-subtle)]"><Save className="h-4 w-4 mr-1.5" />Save as report</Button>} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="ai-period" />
        <Button onClick={generate} disabled={busy} data-testid="generate-overview-button" className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]">
          <RefreshCw className={cn("h-4 w-4 mr-1.5", busy && "animate-spin")} />{result ? "Regenerate" : "Generate overview"}
        </Button>
      </div>

      {busy && !result && (<div className="space-y-3"><Skeleton className="h-28 rounded-[14px]" /><div className="grid grid-cols-1 lg:grid-cols-3 gap-4">{[0,1,2].map((i) => <Skeleton key={i} className="h-64 rounded-[14px]" />)}</div></div>)}

      {!busy && !result && (
        <SectionCard testid="ai-empty"><EmptyState icon={Sparkles} title={`Generate your ${horizon.toLowerCase()} overview`} message="The Strategist reads your Wealth, Life and Wellness centers and tells you how you're doing, what to focus on, and the next best steps." action={<Button onClick={generate} className="bg-[var(--green)] text-[#f7fbf9]" data-testid="generate-overview-empty"><Sparkles className="h-4 w-4 mr-1.5" />Generate overview</Button>} /></SectionCard>
      )}

      {result && (
        <div>
          <div className="mb-4 rounded-[var(--radius-card)] border border-[color:rgba(198,139,60,0.35)] bg-[color:rgba(198,139,60,0.08)] p-5" data-testid="ai-headline-card">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]"><Sparkles className="h-3.5 w-3.5" />{horizon} overview · {periodLabel(horizon, date)}</div>
            <h3 className="font-serif-display text-2xl text-[var(--text)] mt-2" data-testid="ai-headline">{result.headline}</h3>
            <p className="text-sm text-[var(--text)] mt-1.5 leading-relaxed" data-testid="ai-summary">{result.summary}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {(result.areas || []).map((a) => { const st = statusStyles[a.status] || statusStyles.watch; const color = centerMeta[a.area]?.color || "var(--green)"; return (
              <SectionCard key={a.area} testid={`ai-area-${a.area}`}>
                <div className="flex items-center justify-between">
                  <div className="font-serif-display text-lg text-[var(--text)]">{a.area}</div>
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs" style={{ backgroundColor: st.bg, color: st.color }} data-testid={`ai-status-${a.area}`}>{st.label}</span>
                </div>
                <p className="text-[13px] text-[var(--text-muted)] mt-2 leading-relaxed">{a.assessment}</p>
                {a.focus && <p className="text-sm text-[var(--text)] mt-3"><span className="font-medium" style={{ color }}>Focus:</span> {a.focus}</p>}
                {(a.actions || []).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {a.actions.map((act, i) => (<li key={i} className="flex items-start gap-2 text-[13px] text-[var(--text)]"><ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color }} />{act}</li>))}
                  </ul>
                )}
              </SectionCard>
            ); })}
          </div>

          {(result.priorities || []).length > 0 && (
            <SectionCard title="Top priorities" subtitle={`What matters most this ${horizon.toLowerCase()}`} testid="ai-priorities-card">
              <ul className="space-y-2">
                {result.priorities.map((p, i) => (<li key={i} className="flex items-start gap-3 rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`ai-priority-${i}`}><span className="grid place-items-center h-6 w-6 rounded-full bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] text-xs font-semibold shrink-0">{i + 1}</span><span className="text-sm text-[var(--text)]">{p}</span></li>))}
              </ul>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}
